import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Coffee, MapPin, Clock, Phone, Mail, User, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SignUp = () => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    owner_name: '',
    contact_number: '',
    email: '',
    address: '',
    description: '',
    hours: '',
    specialties: '',
    image_url: '',
    latitude: 0,
    longitude: 0
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const specialtiesArray = formData.specialties
        .split(',')
        .map(s => s.trim())
        .filter(s => s.length > 0);

      const submissionData = {
        ...formData,
        specialties: specialtiesArray,
        latitude: parseFloat(formData.latitude) || 15.1500,
        longitude: parseFloat(formData.longitude) || 120.5900
      };

      await axios.post(`${API}/submissions`, submissionData);
      
      // Send email notification to culiatcoffee@gmail.com
      // Note: In production, this would be handled by the backend
      console.log('Submission sent to: culiatcoffee@gmail.com');
      
      setSubmitted(true);
      setLoading(false);
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Failed to submit. Please try again.');
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen grain-overlay flex items-center justify-center px-6 py-20">
        <Card className="max-w-2xl w-full border-2 border-accent/20">
          <CardContent className="p-12 text-center">
            <CheckCircle className="h-20 w-20 text-accent mx-auto mb-6" strokeWidth={1.5} />
            <h2 className="text-3xl font-semibold mb-4" data-testid="success-title">
              Submission Received!
            </h2>
            <p className="text-lg text-muted-foreground mb-6">
              Thank you for submitting your coffee shop. Our team will review your submission and contact you within 2-3 business days.
            </p>
            <p className="text-sm text-muted-foreground mb-6">
              A confirmation has been sent to <span className="font-medium text-foreground">culiatcoffee@gmail.com</span>
            </p>
            <Button
              onClick={() => window.location.href = '/'}
              className="bg-accent hover:bg-accent/90"
              data-testid="back-home-button"
            >
              Back to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen grain-overlay">
      <div className="px-6 md:px-12 lg:px-24 py-16">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-sm font-medium text-accent uppercase tracking-wider">Join Our Directory</span>
            <h1 className="text-4xl md:text-5xl font-semibold mt-3 mb-4" data-testid="signup-title">
              Add Your Coffee Shop
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Showcase your cafe to coffee lovers in Angeles City. Fill out the form below and we'll review your submission.
            </p>
          </div>

          <Card className="border-2 border-border shadow-lg">
            <CardHeader className="border-b border-border bg-muted/20">
              <CardTitle className="text-2xl">Coffee Shop Information</CardTitle>
            </CardHeader>
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium mb-2">
                      <Coffee className="h-4 w-4 text-accent" />
                      Coffee Shop Name *
                    </label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="e.g., The Daily Grind"
                      required
                      data-testid="input-name"
                    />
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium mb-2">
                      <User className="h-4 w-4 text-accent" />
                      Owner Name *
                    </label>
                    <Input
                      name="owner_name"
                      value={formData.owner_name}
                      onChange={handleChange}
                      placeholder="Your full name"
                      required
                      data-testid="input-owner-name"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium mb-2">
                      <Phone className="h-4 w-4 text-accent" />
                      Contact Number *
                    </label>
                    <Input
                      name="contact_number"
                      value={formData.contact_number}
                      onChange={handleChange}
                      placeholder="+63 912 345 6789"
                      required
                      data-testid="input-contact"
                    />
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium mb-2">
                      <Mail className="h-4 w-4 text-accent" />
                      Email Address *
                    </label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      required
                      data-testid="input-email"
                    />
                  </div>
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-medium mb-2">
                    <MapPin className="h-4 w-4 text-accent" />
                    Full Address *
                  </label>
                  <Input
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="123 Main Street, Angeles City, Pampanga"
                    required
                    data-testid="input-address"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Description *
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Tell us about your coffee shop..."
                    required
                    rows="4"
                    className="w-full px-4 py-3 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
                    data-testid="input-description"
                  />
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-medium mb-2">
                    <Clock className="h-4 w-4 text-accent" />
                    Operating Hours *
                  </label>
                  <Input
                    name="hours"
                    value={formData.hours}
                    onChange={handleChange}
                    placeholder="e.g., Mon-Fri: 7am-7pm, Sat-Sun: 8am-8pm"
                    required
                    data-testid="input-hours"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Specialties (comma-separated) *
                  </label>
                  <Input
                    name="specialties"
                    value={formData.specialties}
                    onChange={handleChange}
                    placeholder="e.g., Espresso, Latte Art, Cold Brew"
                    required
                    data-testid="input-specialties"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Separate each specialty with a comma
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Photo URL (optional)
                  </label>
                  <Input
                    name="image_url"
                    value={formData.image_url}
                    onChange={handleChange}
                    placeholder="https://example.com/photo.jpg"
                    data-testid="input-image-url"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Link to your coffee shop's photo
                  </p>
                </div>

                <div className="pt-4 border-t border-border">
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full h-14 bg-accent hover:bg-accent/90 text-accent-foreground font-medium text-base"
                    data-testid="submit-button"
                  >
                    {loading ? 'Submitting...' : 'Submit for Review'}
                  </Button>
                  <p className="text-sm text-muted-foreground text-center mt-4">
                    By submitting, you agree to our terms and conditions
                  </p>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
